import _, {chain, find, isEqual, xorWith} from 'lodash';
import memoize from 'lru-memoize';
import {DateTime} from 'luxon';

import {isSubsetArray} from '../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {build12Or24hOptionsForFormat, TimeFormatsEnum} from '../../../src/helpers/dateTimeHelpers';
import {isWriteAccessCalendarVersion} from '../../../src/helpers/models/calendarVersionHelpers';
import {
  parseRecurrenceRuleFromString,
  toEpochMillisFromRRuleLibraryDate,
} from '../../../src/helpers/recurrenceHelpers';
import {CalendarEventModel} from '../../../src/models/api/calendarEventModel';
import {
  CalendarEventVersionModel,
  CalendarEventVersionReminder,
  CalendarEventVersionShowAsTypesEnum,
  CalendarEventVersionStartEnd,
  CalendarEventVersionVisibilityTypesEnum,
  CalendarEventVersionVisualStatusesEnum,
  CompleteCalendarEventVersionModel,
} from '../../../src/models/api/calendarEventVersionModel';
import {CalendarModel, CalendarProviderTypesEnum} from '../../../src/models/api/calendarModel';
import {
  CalendarVersionAccessRolesEnum,
  CalendarVersionModel,
} from '../../../src/models/api/calendarVersionModel';
import {CompanyModel} from '../../../src/models/api/companyModel';
import {ParticipantTypesEnum} from '../../../src/models/api/participantModel';
import {buildPublicId} from '../../../src/models/applications/publicIdTypes';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../src/services/tracking';
import {ResourceMap} from '../../../src/store/appTypes';
import {CalendarEventSaveData} from '../../../src/store/data/calendarEventVersions/calendarEventVersionsActions';
import {CalendarEventData} from '../../../src/store/panels/calendars/calendarsPanelSelectors';
import {CalendarsParameters} from '../../../src/store/panels/calendars/calendarsParameters';
import {logger} from '../../services/logger/logger';

/*
 * Constants.
 */

const maxHoursBetweenOriginalStartAndInstanceStart = 24;

/*
 * Computed properties.
 */

export function buildCalendarEventVersionPublicId(calendarEventVersion: CompleteCalendarEventVersionModel) {
  return buildPublicId('calendarEventVersion', calendarEventVersion.id);
}

/*
 * Helpers.
 */

export enum CalendarEventTimeTypesEnum {
  START = 'START',
  END = 'END',
}

export function makeCalendarEventVersionDateTime(
  startEnd: CalendarEventVersionStartEnd,
  type: CalendarEventTimeTypesEnum,
) {
  if (startEnd.isAllDay) {
    const {start, end} = startEnd;
    const time = type === CalendarEventTimeTypesEnum.START ? start : end;
    return DateTime.fromISO(time.date);
  }

  const {start, end} = startEnd;
  const time = type === CalendarEventTimeTypesEnum.START ? start : end;
  return DateTime.fromISO(time.dateTime);
}

function getInstanceTimesInRange(
  recurrenceWithoutExDate: ReadonlyArray<string>,
  startsAt: number,
  optionalTimezone: string | null | undefined,
  rangeStartsAt: number,
  rangeEndsAt: number,
) {
  return recurrenceWithoutExDate.length > 0
    ? getInstanceTimesInRangeMemoized(
        recurrenceWithoutExDate.join('\n'),
        startsAt,
        optionalTimezone,
        rangeStartsAt,
        rangeEndsAt,
      )
    : [];
}

// Since rrule.between is one of the major bottlenecks in event rendering speed,
// we memoize the instance time computations.
const getInstanceTimesInRangeMemoized = memoize(2000)(
  (
    recurrenceString: string,
    startsAt: number,
    optionalTimezone: string | null | undefined,
    rangeStartsAt: number,
    rangeEndsAt: number,
  ) => {
    const ruleSet = parseRecurrenceRuleFromString(recurrenceString, {
      timezone: optionalTimezone,
      startsAt,
    });
    return ruleSet
      .between(new Date(rangeStartsAt), new Date(rangeEndsAt))
      .map(toEpochMillisFromRRuleLibraryDate);
  },
);

// TODO: rename to use a word like "build" instead of "make" since "make" is reserved for selector factories.
export function makeStartEndDateTimeForCalendarEventVersion(
  calendarEventVersion: CalendarEventVersionModel,
  instanceStartDateTime?: DateTime,
) {
  const startDateTime = makeCalendarEventVersionDateTime(
    calendarEventVersion,
    CalendarEventTimeTypesEnum.START,
  );
  const endDateTime = makeCalendarEventVersionDateTime(calendarEventVersion, CalendarEventTimeTypesEnum.END);

  // If an instanceDateTime is provided, we use that compute the event's start and end,
  // since the event represents an instance within a recurring event series.
  if (instanceStartDateTime) {
    const duration = endDateTime.diff(startDateTime);
    return {
      startDateTime: instanceStartDateTime,
      endDateTime: instanceStartDateTime.plus(duration),
    };
  }

  return {
    startDateTime,
    endDateTime,
  };
}

export function getRecurringEventInstanceStartTimes(
  masterCalendarEventVersion: CalendarEventVersionModel,
  masterCalendarEvent: CalendarEventModel,
  exceptionCalendarEvents: CalendarEventModel[],
  rangeStartsAt: number,
  rangeEndsAt: number,
  hasNewRecurrenceClient?: boolean,
): number[] {
  // TODO PB-36446: we won't need to do this kind of branching once we strictly use CEV recurrence.
  const cevRecurrence = masterCalendarEventVersion.ical_recurrence;
  const ceRecurrence = masterCalendarEvent.recurrence;

  const recurrence = hasNewRecurrenceClient ? cevRecurrence ?? ceRecurrence : ceRecurrence;
  if (!recurrence) {
    return [];
  }

  const {isAllDay} = masterCalendarEventVersion;

  // We explicitly set the timezone for non-all-day events because the event's timezone
  // impacts how the series is expanded. If the timezone is PST for example,
  // the expanded instances must start at the same hour in PST every day/week/etc even when
  // crossing daylight savings boundaries. If you viewed the same event in the Hawaii-Aleutian timezone,
  // which does not respect daylight savings, the instance event start times should actually shift by
  // an hour when crossing the DST boundary.
  const optionalTimezone = isAllDay ? null : masterCalendarEventVersion.start.timezone;

  // Get the start of the series from which all instances are computed.
  const eventVersionStartTime = makeCalendarEventVersionDateTime(
    masterCalendarEventVersion,
    CalendarEventTimeTypesEnum.START,
  ).toMillis();

  const rangeStartDateTime = DateTime.fromMillis(rangeStartsAt);
  const rangeEndDateTime = DateTime.fromMillis(rangeEndsAt);

  try {
    // For memoization gains, request instance times over a full year range and then filter
    // the times we need for the current view.
    const recurringInstanceStartTimes = getInstanceTimesInRange(
      recurrence,
      eventVersionStartTime,
      optionalTimezone,
      rangeStartDateTime.startOf('year').toMillis(),
      rangeEndDateTime.endOf('year').toMillis(),
    ).filter((t) => t > rangeStartDateTime.toMillis() && t < rangeEndDateTime.toMillis());

    // Get the original start times of exception events.
    const exceptionStartTimes = _(exceptionCalendarEvents)
      .map((event) => {
        if (!event.originalStartDate) {
          return null;
        }

        if (!isAllDay) {
          return event.originalStartDate;
        }

        return getAllDayOriginalStartDate(
          event.originalStartDate,
          recurringInstanceStartTimes,
          masterCalendarEventVersion.start.timezone,
        );
      })
      .compact()
      .value();

    // Include the master event version start time since there are cases in which it differs from the
    // recurrence rule and would not be included in recurringInstanceStartTimes.
    return chain([eventVersionStartTime, ...recurringInstanceStartTimes])
      .difference(exceptionStartTimes)
      .sort()
      .sortedUniq()
      .value();
  } catch (e) {
    logger.warn(
      `Failed to parse recurrence rule for calendar event: ${masterCalendarEvent.uid}. Recurrence: ${recurrence}`,
    );
    return [];
  }
}

// For all-day events, we generate an originalStart timestamp that's
// comparable with the output of ruleSet.between by creating a local DateTime
// that has the same day/hour/minute as the the originalStartDate.
function getAllDayOriginalStartDate(
  originalStartDate: number,
  allInstanceStartDates: number[],
  recurrenceTimezone?: string,
) {
  if (recurrenceTimezone) {
    // If a timezone is present for an all-day recurring event, we expect that the
    // originalStart is midnight in that timezone.
    if (!isMidnightInTimezone(originalStartDate, recurrenceTimezone)) {
      throw new Error('All-day exception event has an unexpected originalStartDate');
    }

    return DateTime.fromMillis(originalStartDate)
      .setZone(recurrenceTimezone) // midnight in the event's timezone
      .setZone('local', {keepLocalTime: true}) // same year/month/day midnight in local timezone
      .valueOf();
  }

  if (isMidnightInTimezone(originalStartDate, 'utc')) {
    return DateTime.fromMillis(originalStartDate).toUTC().setZone('local', {keepLocalTime: true}).valueOf();
  }

  // If there is no recurrence timezone and the exception event is not midnight UTC,
  // we make an educated guess of what the originalStart is by:
  //  1. getting the nearest midnight to the originalStartDate in the local timezone
  //  2. finding the nearest entry to that in allInstanceStartDates
  //
  // Note: this will result in an incorrect originalStart being returned when the series has a daily
  // recurrence and the user's local timezone is over 12 hours away from the actual timezone of
  // the recurrence. When this happens, we will exclude occurrences that are adjacent to
  // exception events from being rendered.
  const nearestMidnight = getNearestMidnight(DateTime.fromMillis(originalStartDate));
  const nearestInstantStartValue = getNearestValue(allInstanceStartDates, nearestMidnight.valueOf());
  if (!nearestInstantStartValue) {
    return null;
  }

  const nearestInstanceStart = DateTime.fromMillis(nearestInstantStartValue);

  // If nearestInstanceStart is > 24 hours away from nearestMidnightDateTime,
  // something unexpected has happened and we don't want this exception to take
  // the place of that instance.
  const difference = nearestMidnight.diff(nearestInstanceStart, 'hours');
  if (Math.abs(difference.hours) > maxHoursBetweenOriginalStartAndInstanceStart) {
    return null;
  }

  return nearestInstanceStart.valueOf();
}

function getNearestValue(haystack: number[], needle: number) {
  if (haystack.length === 0) {
    return null;
  }

  return haystack.reduce((prev, curr) => {
    if (Math.abs(curr - needle) < Math.abs(prev - needle)) {
      return curr;
    }

    return prev;
  });
}

function getNearestMidnight(dateTime: DateTime) {
  return dateTime.hour < 12 ? dateTime.startOf('day') : dateTime.endOf('day');
}

function isMidnightInTimezone(timestamp: number, timezone: string) {
  const dateTime = DateTime.fromMillis(timestamp).setZone(timezone);
  return dateTime.equals(dateTime.startOf('day'));
}

export function isFocusedCalendarEvent(
  calendarEventVersionUid: string,
  calendarVersionId: number,
  eventInstanceStartDate: number | undefined,
  focusedCalendarEventData: CalendarEventData | undefined,
) {
  if (!focusedCalendarEventData) {
    return false;
  }

  const {calendarEventVersion, calendarVersion, instanceStartDate} = focusedCalendarEventData;

  return (
    calendarEventVersion.uid === calendarEventVersionUid &&
    calendarVersion.id === calendarVersionId &&
    instanceStartDate === eventInstanceStartDate
  );
}

export function isEventInRange(
  calendarEventVersion: CalendarEventVersionModel,
  rangeStartsAt: number,
  rangeEndsAt: number,
) {
  const startDateTime = makeCalendarEventVersionDateTime(
    calendarEventVersion,
    CalendarEventTimeTypesEnum.START,
  );
  const endDateTime = makeCalendarEventVersionDateTime(calendarEventVersion, CalendarEventTimeTypesEnum.END);

  return Boolean(startDateTime.valueOf() < rangeEndsAt && endDateTime.valueOf() > rangeStartsAt);
}

export function buildDefaultShowAsForIsAllDay(isAllDay: boolean) {
  return isAllDay ? CalendarEventVersionShowAsTypesEnum.FREE : CalendarEventVersionShowAsTypesEnum.BUSY;
}

export function buildDraftCalendarEventVersion(
  uid: string,
  url: string,
  calendarEventUid: string,
  calendarVersionId: number,
  startDateTime: DateTime,
  endDateTime: DateTime,
  isAllDay: boolean,
  summary?: string,
): CalendarEventVersionModel {
  return {
    uid,
    calendarEventVersionUrl: url,
    calendarEventUid,
    channelId: undefined,
    calendarVersionIds: [],
    summary: summary || undefined,
    description: undefined,
    locations: [],
    conference: undefined,
    color: undefined,
    showAs: buildDefaultShowAsForIsAllDay(isAllDay),
    visibility: CalendarEventVersionVisibilityTypesEnum.DEFAULT,
    recurrence: undefined,
    ical_recurrence: undefined,
    reminders: undefined,
    response: undefined,
    isRemoved: false,
    visualStatus: CalendarEventVersionVisualStatusesEnum.DRAFT,
    isSaving: false,
    includesTeammateReschedulingLink: false,
    isOrganizerCopy: true,
    updatedAt: 0,
    ...buildCalendarEventVersionStartEndTime(startDateTime, endDateTime, isAllDay),
  };
}

export function buildCalendarEventVersionUrl(company: CompanyModel, uid: string) {
  return `${company.url}/calendar_event_versions/${uid}`;
}

export function buildCalendarEventVersionStartEndTime(
  startDateTime: DateTime,
  endDateTime: DateTime,
  isAllDay: boolean,
) {
  if (isAllDay) {
    return {
      isAllDay: true as const,
      start: buildCalendarEventVersionDate(startDateTime),
      end: buildCalendarEventVersionDate(endDateTime),
    };
  }

  return {
    isAllDay: false as const,
    start: buildCalendarEventVersionDateTime(startDateTime),
    end: buildCalendarEventVersionDateTime(endDateTime),
  };
}

function buildCalendarEventVersionDateTime(dateTime: DateTime) {
  return {
    dateTime: dateTime.toISO(),
    timezone: undefined,
  };
}

function buildCalendarEventVersionDate(dateTime: DateTime) {
  return {
    date: dateTime.toFormat('yyyy-MM-dd'),
    timezone: undefined,
  };
}

export function makeCalendarEventVersionColor(
  calendarEventVersion: CalendarEventVersionModel,
  calendarVersion: CalendarVersionModel,
) {
  if (isWriteAccessCalendarVersion(calendarVersion) && calendarEventVersion.color) {
    return calendarEventVersion.color;
  }

  return calendarVersion.color;
}

export function buildCalendarEventVersionLocationText(calendarEventVersion: CalendarEventVersionModel) {
  // If we have multiple locations (possible in outlook), combine them into a single location string.
  return calendarEventVersion.locations.map((l) => l.displayName).join(', ');
}

export function isUnsavedDraftCalendarEventVersion(calendarEventVersion: CalendarEventVersionModel) {
  return (
    calendarEventVersion.visualStatus === CalendarEventVersionVisualStatusesEnum.DRAFT &&
    !calendarEventVersion.isSaving
  );
}

export function trackSaveInterfaceEvent(data: CalendarEventSaveData, calendars: ResourceMap<CalendarModel>) {
  const {calendarVersion, newCalendarEventVersion, visualParticipants} = data;

  const calendar = calendars[calendarVersion.calendarId];
  if (!calendar) {
    return;
  }

  const eventTrackingDetails = {
    has_summary: Boolean(newCalendarEventVersion.summary),
    has_location: newCalendarEventVersion.locations.length > 0,
    has_description: Boolean(newCalendarEventVersion.description),
    is_all_day: newCalendarEventVersion.isAllDay,
    has_meeting_room: Boolean(find(visualParticipants, (p) => p.type === ParticipantTypesEnum.RESOURCE)),
    attendee_count: visualParticipants.filter((p) => p.isAttendee).length,
    calendar_provider: calendar.provider,
    notify_attendees: data.shouldNotifyGuests,
  };

  trackInterfaceEvent(
    newCalendarEventVersion.visualStatus === CalendarEventVersionVisualStatusesEnum.DRAFT
      ? InterfaceEventsEnum.CREATE_CALENDAR_EVENT
      : InterfaceEventsEnum.UPDATE_CALENDAR_EVENT,
    eventTrackingDetails,
  );
}

export function buildCalendarEventVersionRemindersToSave(
  calendarVersionReminders: ReadonlyArray<CalendarEventVersionReminder>,
  originalReminders: ReadonlyArray<CalendarEventVersionReminder> | undefined,
  newReminders: ReadonlyArray<CalendarEventVersionReminder>,
) {
  // If this event did not originally have any reminder overrides and the
  // new reminders are the same as the calendarVersion's default calendar reminders,
  // we should not save any reminder overrides.
  const noReminderOverrides = xorWith(newReminders, calendarVersionReminders, isEqual).length === 0;
  if (!Array.isArray(originalReminders) && noReminderOverrides) {
    return undefined;
  }

  return newReminders;
}

// Formats times like "Tue, Jul 18, 2018".
const allDayDateFormat: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

// Formats times like "Tue, Jul 18, 2017 10:30 AM".
const dateAndTimeFormatBase: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  year: 'numeric',
  weekday: 'short',
};

const sameDayDateAndTimeFormatBase: Intl.DateTimeFormatOptions = {
  timeZoneName: 'short',
  ...dateAndTimeFormatBase,
};

const sameDayTimeFormatBase: Intl.DateTimeFormatOptions = {
  timeZoneName: 'short',
  hour: 'numeric',
  minute: 'numeric',
};

const timeWithoutTimezoneFormatBase: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: 'numeric',
};

export const calendarEventTimeFormat = {
  allDayDate: allDayDateFormat,
  dateAndTimeBase: dateAndTimeFormatBase,
  sameDayDateAndTimeBase: sameDayDateAndTimeFormatBase,
  sameDayTimeBase: sameDayTimeFormatBase,
  timeWithoutTimezoneBase: timeWithoutTimezoneFormatBase,
};

export function buildCalendarEventTimeText(
  calendarEventVersion: CalendarEventVersionModel,
  timeFormat: TimeFormatsEnum,
) {
  // Four cases handled:
  // 1. All day event (1 day): "Tue, Jul 18, 2018"
  // 2. All day event (>1 day): "Tue, Dec 2, 2018 - Wed Dec 3, 2018"'
  // 3. Start and end on same day: "Tue, Jul 18, 2017 10:30 AM - 11:30 AM PST".
  // 4. Start and end on different days: "Tue, Jul 18, 2017 10:30 AM - Tue, Jul 20, 2017 12:00 PM PST"
  const startDateTime = makeCalendarEventVersionDateTime(
    calendarEventVersion,
    CalendarEventTimeTypesEnum.START,
  );
  const endDateTime = makeCalendarEventVersionDateTime(calendarEventVersion, CalendarEventTimeTypesEnum.END);

  if (calendarEventVersion.isAllDay) {
    const days = endDateTime.diff(startDateTime, 'days');
    if (days.get('days') <= 1) {
      return startDateTime.toLocaleString(allDayDateFormat);
    }

    return `${startDateTime.toLocaleString(allDayDateFormat)} - ${endDateTime
      .minus({days: 1})
      .toLocaleString(allDayDateFormat)}`;
  }

  const sameDayTimeFormat = decorateFormat(sameDayTimeFormatBase, timeFormat);
  const sameDayDateAndTimeFormat = decorateFormat(sameDayDateAndTimeFormatBase, timeFormat);
  const dateAndTimeFormat = decorateFormat(dateAndTimeFormatBase, timeFormat);
  // Use abbreviated format if this event spans a single day.
  const singleDayEvent = startDateTime.hasSame(endDateTime, 'day');
  const endDateTimeFormat = singleDayEvent ? sameDayTimeFormat : sameDayDateAndTimeFormat;

  return `${startDateTime.toLocaleString(dateAndTimeFormat)} - ${endDateTime.toLocaleString(
    endDateTimeFormat,
  )}`;
}

function decorateFormat(formatOptions: Intl.DateTimeFormatOptions, timeFormat: TimeFormatsEnum) {
  return {
    ...formatOptions,
    ...build12Or24hOptionsForFormat(timeFormat),
  };
}

export function isEventEditable(
  calendar: CalendarModel,
  calendarVersion: CalendarVersionModel,
  calendarEventVersion: CalendarEventVersionModel,
) {
  if (!isWriteAccessCalendarVersion(calendarVersion)) {
    return false;
  }

  // Depending on the calendar, private events may only be editable by an owner calendarVersion.
  if (calendarEventVersion.visibility === CalendarEventVersionVisibilityTypesEnum.PRIVATE) {
    return (
      calendarVersion.accessRole === CalendarVersionAccessRolesEnum.OWNER ||
      calendar.behaviors.writerCanEditPrivateEvents
    );
  }

  return true;
}

export function calendarCanEditGlobalSettings(
  calendar: CalendarModel,
  canEditEvent: boolean,
  isEventOrganizer: boolean,
) {
  // Outlook does not allow non-organizers to edit global properties like topic/location/etc.
  if (calendar.provider === CalendarProviderTypesEnum.OFFICE365) {
    return canEditEvent && isEventOrganizer;
  }

  return canEditEvent;
}

export function getCalendarEventReminders(
  calendarVersion: CalendarVersionModel,
  calendarEventVersion: CalendarEventVersionModel,
) {
  // If this is a write access calendar and we have event override reminders, use those.
  // Otherwise, use the reminders on the calendarVersion.
  if (isWriteAccessCalendarVersion(calendarVersion) && calendarEventVersion.reminders) {
    return calendarEventVersion.reminders;
  }

  return calendarVersion.reminders;
}

export function isEqualOrSubsetCalendarParameters(
  previousParameters: CalendarsParameters,
  newParameters: CalendarsParameters,
): boolean {
  // Compare focusedDate, viewType, weekStartDay (must be same as before).
  if (
    !isEqual(
      [previousParameters.focusedDate, previousParameters.viewType, previousParameters.weekStartDay],
      [newParameters.focusedDate, newParameters.viewType, newParameters.weekStartDay],
    )
  ) {
    return false;
  }
  // Compare selectedCalendarVersionIds (allow subset).
  if (
    !isSubsetArray(previousParameters.selectedCalendarVersionIds, newParameters.selectedCalendarVersionIds)
  ) {
    return false;
  }
  // Compare viewingNowCalendarVersionIds (allow subset).
  if (
    !isSubsetArray(
      previousParameters.viewingNowCalendarVersionIds,
      newParameters.viewingNowCalendarVersionIds,
    )
  ) {
    return false;
  }
  return true;
}
